import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Save, Loader2, Plus } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { PERIODS } from '../../utils/financialPeriods';
import { userPrefs, PREFS } from '../../utils/userPreferences';
import { bomService } from '../../services/bomService';
import { requestedStockService } from '../../services/requestedStockService';
import MantleDataGrid, { gcell } from '../../components/MantleDataGrid';
import Field from '../../components/Field';
import {
  SCOPES, NODE_TO_LINE, fmtQty, fmtInt, isBatch, periodsSummary,
  buildTree, findNode, collectIds, flattenVisible, collectWips,
  inputClass, pillClass, LineDetailModal, AddComponentModal, NewBomModal,
} from './bomParts';

// =============================================================================
// Bill of Materials — wired to bud_bom (budgeted BOM for the active version).
//
// The multi-level explosion is rendered with MantleDataGrid (Glide), the house
// grid used by Structures / GL Mapping / Budget Grid, so the BOM gets the same
// canvas rendering, search, range copy/paste and resizable columns as every
// other grid in Mantle. The tree logic, the maths and the three dialogs live in
// bomParts.jsx.
//
// Two things follow from being a grid rather than a tree of <tr>s:
//   * Fields that used to be modal-only (fixed loss, work centre, issue
//     warehouse, notes) are COLUMNS — editable without opening a line. The line
//     detail modal still owns validity and per-item production settings.
//   * A row click can't open the modal (in a grid a click selects a cell), so
//     expand/collapse lives on the Component cell and the modal opens from the
//     "⋯" column or the Flags cell.
// =============================================================================

// Per-cell theme accents, same idea as pages/People/Structures.jsx.
const T_CALC = { bgCell: 'rgba(100, 116, 139, 0.10)' };
const T_BATCH = { textDark: '#0891b2', bgCell: 'rgba(6, 182, 212, 0.10)' };
const T_MUTED = { textDark: '#94a3b8' };
const T_ACTION = { textDark: '#94a3b8', bgCell: 'rgba(100, 116, 139, 0.06)' };
const BUBBLE_BY_TYPE = {
  FG:  { bgBubble: 'rgba(16, 185, 129, 0.16)', textBubble: '#047857' },
  WIP: { bgBubble: 'rgba(245, 158, 11, 0.16)', textBubble: '#b45309' },
  RM:  { bgBubble: 'rgba(14, 165, 233, 0.16)', textBubble: '#0369a1' },
  PKG: { bgBubble: 'rgba(139, 92, 246, 0.16)', textBubble: '#6d28d9' },
};

// Tree indent for a canvas cell. Glide paints text, so the indent has to live in
// the string — em+en space per level (~20px, matching the old table's 22px).
const INDENT = '  '; // em + en space, ~20px per level (the old table used 22px)
const treeLabel = (row, isOpen) => {
  const twisty = row.children?.length ? (isOpen ? '▾ ' : '▸ ') : '   ';
  return `${INDENT.repeat(row.depth)}${twisty}${row.code} · ${row.description}`;
};

const COLUMNS = [
  { id: 'component', title: 'Component', width: 380 },
  { id: 'type',      title: 'Type',      width: 74 },
  { id: 'flags',     title: 'Flags',     width: 160 },
  { id: 'uom',       title: 'UoM',       width: 62 },
  { id: 'qtyPer',    title: 'Qty Per',   width: 96 },
  { id: 'scrap',     title: 'Scrap %',   width: 84 },
  { id: 'fixedLoss', title: 'Fixed Loss', width: 96 },
  { id: 'ext',       title: 'Ext Qty',   width: 112 },
  { id: 'workCenter', title: 'Work Centre', width: 118 },
  { id: 'warehouse', title: 'Issue W/H', width: 118 },
  { id: 'notes',     title: 'Notes',     width: 220 },
  { id: 'detail',    title: '',          width: 44 },
];
const COL_IDS = COLUMNS.map((c) => c.id);

const WIP_COLUMNS = [
  { id: 'stage',  title: 'WIP Stage', width: 250 },
  { id: 'qtyPer', title: 'Qty Per',   width: 96 },
  { id: 'scrap',  title: 'Scrap %',   width: 84 },
];
const WIP_COL_IDS = WIP_COLUMNS.map((c) => c.id);

const BillOfMaterials = () => {
  const { fyStartMonth, selectedVersion, selectedSite } = useAppContext();

  // ---- Data from the server (source of truth for edits) ----
  const [catalog, setCatalog] = useState({});
  const [linesByParent, setLinesByParent] = useState({});
  const [mfg, setMfg] = useState({});
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // ---- View state ----
  const [scope, setScope] = useState(() => userPrefs.get(PREFS.BOM_SCOPE, 'FG'));
  const [stockCode, setStockCode] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [detailId, setDetailId] = useState(null);

  // ---- Dirty tracking for save ----
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const dirtyLines = useRef(new Set());
  const dirtySettings = useRef(new Set());

  // ---- Add-component picker ----
  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  const [stockOptions, setStockOptions] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const stockLoaded = useRef(false);
  // Confirmation shown after a "request a new stock code" is filed, so the
  // planner sees that it went somewhere rather than the dialog just closing.
  const [requestedNote, setRequestedNote] = useState('');

  const [newOpen, setNewOpen] = useState(false);

  useEffect(() => { userPrefs.set(PREFS.BOM_SCOPE, scope); }, [scope]);

  // ---- Load the whole BOM for the active version ----
  const load = useCallback(async () => {
    if (!selectedVersion || !selectedSite) { setLoading(false); setLoadError(''); return; }
    setLoading(true);
    setLoadError('');
    try {
      const res = await bomService.getBom();
      if (res?.success) {
        const { items = [], lines = [], settings = [], warehouses: whs = [] } = res.data || {};
        const cat = {};
        for (const it of items) cat[it.code] = { description: it.description, type: it.type, uom: it.uom };
        const lbp = {};
        for (const l of lines) {
          if (!lbp[l.parent_code]) lbp[l.parent_code] = [];
          lbp[l.parent_code].push(l);
        }
        const m = {};
        for (const code of Object.keys(cat)) {
          if (cat[code].type === 'FG' || cat[code].type === 'WIP') m[code] = { basis: 'unit', batchSize: 0 };
        }
        for (const s of settings) m[s.stock_code] = { basis: s.basis, batchSize: Number(s.batch_size) };

        setCatalog(cat);
        setLinesByParent(lbp);
        setMfg(m);
        setWarehouses(whs);
      } else {
        setLoadError(res?.message || 'Failed to load the BOM.');
      }
    } catch (e) {
      setLoadError(e.response?.data?.message || e.message || 'Failed to load the BOM.');
    } finally {
      dirtyLines.current.clear();
      dirtySettings.current.clear();
      setDirty(false);
      setStockCode('');
      setExpanded(new Set());
      setDetailId(null);
      setLoading(false);
    }
  }, [selectedVersion, selectedSite]);

  useEffect(() => { load(); }, [load]);

  // ---- Derived data ----
  const manufacturedSet = useMemo(
    () => new Set(Object.keys(catalog).filter((c) => catalog[c].type === 'FG' || catalog[c].type === 'WIP')),
    [catalog]
  );
  const isManufactured = useCallback((code) => manufacturedSet.has(code), [manufacturedSet]);
  const existingBomParents = useMemo(() => Object.keys(linesByParent), [linesByParent]);

  const itemsInScope = useCallback(
    (sc) => existingBomParents.filter((c) => catalog[c] && (sc === 'ALL' || catalog[c].type === sc)).sort(),
    [existingBomParents, catalog]
  );

  const scopedItems = useMemo(() => {
    const set = new Set(itemsInScope(scope));
    if (stockCode && catalog[stockCode]) set.add(stockCode);
    return [...set].sort();
  }, [itemsInScope, scope, stockCode, catalog]);

  const newBomCandidates = useMemo(
    () => [...manufacturedSet]
      .filter((c) => !linesByParent[c])
      .map((c) => ({ code: c, description: catalog[c].description, type: catalog[c].type, uom: catalog[c].uom }))
      .sort((a, b) => a.code.localeCompare(b.code)),
    [manufacturedSet, linesByParent, catalog]
  );

  const usedIn = useCallback(
    (code) => Object.keys(linesByParent).filter((p) => linesByParent[p].some((l) => l.component_code === code)),
    [linesByParent]
  );

  const rootItem = stockCode ? catalog[stockCode] : null;
  const rootMode = stockCode ? mfg[stockCode] : null;
  const rootIsBatch = isBatch(rootMode);

  const tree = useMemo(
    () => (stockCode ? buildTree(stockCode, linesByParent, catalog) : []),
    [stockCode, linesByParent, catalog]
  );

  const rows = useMemo(
    () => flattenVisible(tree, expanded, mfg, rootMode, 0, rootIsBatch ? Number(rootMode.batchSize) : 1),
    [tree, expanded, mfg, rootMode, rootIsBatch]
  );
  const wipStages = useMemo(() => collectWips(tree, rootMode, mfg), [tree, rootMode, mfg]);
  const detailNode = useMemo(() => (detailId ? findNode(tree, detailId) : null), [tree, detailId]);

  const compoundScrap = useMemo(
    () => (wipStages.reduce((acc, w) => acc * (1 + Number(w.scrap || 0) / 100), 1) - 1) * 100,
    [wipStages]
  );
  const rootUsedIn = useMemo(() => (stockCode ? usedIn(stockCode) : []), [stockCode, usedIn]);

  // Grid row indices map to these arrays; refs keep the Glide callbacks stable.
  const rowsRef = useRef([]);
  rowsRef.current = rows;
  const wipRef = useRef([]);
  wipRef.current = wipStages;
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;

  // ---- Selection / expand ----
  const handleSelect = (code) => {
    setStockCode(code);
    const nodes = code ? buildTree(code, linesByParent, catalog) : [];
    setExpanded(new Set(nodes.map((n) => n.id)));
    setDetailId(null);
  };

  const handleScope = (next) => {
    setScope(next);
    if (stockCode && next !== 'ALL' && catalog[stockCode]?.type !== next) handleSelect('');
  };

  const startNewBom = (code) => {
    setNewOpen(false);
    if (catalog[code] && scope !== 'ALL' && catalog[code].type !== scope) setScope(catalog[code].type);
    handleSelect(code);
  };

  const toggleNode = useCallback((id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    }), []);

  const expandAll = () => setExpanded(new Set(collectIds(tree)));
  const collapseAll = () => setExpanded(new Set());

  // ---- Editing (patches the normalized line, so shared edits propagate) ----
  const patchNodeById = useCallback((id, patch) => {
    const node = findNode(tree, id);
    if (!node) return;
    const { parentCode, code: componentCode, lineNo } = node;
    const linePatch = {};
    for (const [k, v] of Object.entries(patch)) {
      const col = NODE_TO_LINE[k];
      if (col) linePatch[col] = v;
    }
    if (Object.keys(linePatch).length === 0) return;

    setLinesByParent((prev) => {
      const arr = prev[parentCode] || [];
      const nextArr = arr.map((l) =>
        l.component_code === componentCode && l.line_no === lineNo ? { ...l, ...linePatch } : l
      );
      return { ...prev, [parentCode]: nextArr };
    });
    dirtyLines.current.add(`${parentCode}|${componentCode}|${lineNo}`);
    setDirty(true);
    setSaveError('');
  }, [tree]);

  const updateNode = useCallback((id, field, value) => {
    const num = value === '' ? 0 : Number(value);
    if (isNaN(num)) return;
    patchNodeById(id, { [field]: num });
  }, [patchNodeById]);

  const patchMfg = (code, patch) => {
    setMfg((prev) => ({ ...prev, [code]: { basis: 'unit', batchSize: 0, ...prev[code], ...patch } }));
    dirtySettings.current.add(code);
    setDirty(true);
    setSaveError('');
  };

  // ---- Main grid ------------------------------------------------------------
  const getCellContent = useCallback(([col, row]) => {
    const r = rowsRef.current[row];
    const id = COL_IDS[col];
    if (!r) return gcell.text('', { readonly: true });

    switch (id) {
      case 'component':
        return gcell.text(treeLabel(r, expandedRef.current.has(r.id)), { readonly: true });
      case 'type':
        return gcell.bubble([r.type], { theme: BUBBLE_BY_TYPE[r.type] });
      case 'flags': {
        // The chips the old tree drew inline: batch control, limited validity,
        // and a marker for a line carrying notes.
        const chips = [];
        const mode = mfg[r.code];
        if (isBatch(mode)) chips.push(`BATCH · ${fmtInt(mode.batchSize)} ${r.uom}`);
        const validity = periodsSummary(r.periods);
        if (validity) chips.push(validity);
        if (r.notes) chips.push('✎');
        return gcell.bubble(chips);
      }
      case 'uom':
        return gcell.text(r.uom, { readonly: true, theme: T_MUTED });
      case 'qtyPer':
        // Cyan = captured per batch of the parent, not per unit.
        return gcell.number(r.qtyPer, { display: fmtQty(r.qtyPer), ...(r.perBatch ? { theme: T_BATCH } : {}) });
      case 'scrap':
        return gcell.number(r.scrap, { display: `${Number(r.scrap || 0)}` });
      case 'fixedLoss':
        return gcell.number(r.fixedLoss, { display: fmtQty(r.fixedLoss) });
      case 'ext':
        return gcell.number(r.ext, { readonly: true, display: fmtQty(r.ext), theme: T_CALC });
      case 'workCenter':
        return gcell.text(r.workCenter || '');
      case 'warehouse':
        return gcell.text(r.warehouse || '');
      case 'notes':
        return gcell.text(r.notes || '');
      case 'detail':
        return gcell.text('⋯', { readonly: true, theme: T_ACTION });
      default:
        return gcell.text('', { readonly: true });
    }
  }, [mfg]);

  const onCellEdited = useCallback(([col, row], newVal) => {
    const r = rowsRef.current[row];
    if (!r) return;
    const id = COL_IDS[col];
    if (id === 'qtyPer' || id === 'scrap' || id === 'fixedLoss') {
      updateNode(r.id, id, newVal.data);
    } else if (id === 'workCenter' || id === 'warehouse' || id === 'notes') {
      patchNodeById(r.id, { [id]: newVal.data ?? '' });
    }
  }, [updateNode, patchNodeById]);

  // Component cell = the twisty (it is read-only, so a click has nothing else to
  // do); Flags and ⋯ open the line detail the old row-click used to open.
  const onCellClicked = useCallback(([col, row]) => {
    const r = rowsRef.current[row];
    if (!r) return;
    const id = COL_IDS[col];
    if (id === 'component') {
      if (r.children?.length) toggleNode(r.id);
    } else if (id === 'detail' || id === 'flags') {
      setDetailId(r.id);
    }
  }, [toggleNode]);

  // ---- WIP stage-losses grid ------------------------------------------------
  const getWipCell = useCallback(([col, row]) => {
    const w = wipRef.current[row];
    const id = WIP_COL_IDS[col];
    if (!w) return gcell.text('', { readonly: true });
    switch (id) {
      case 'stage': {
        const shared = usedIn(w.code);
        const suffix = shared.length > 1 ? `  (shared ×${shared.length})` : '';
        return gcell.text(`${INDENT.repeat(w.depth)}${w.code} · ${w.description}${suffix}`, { readonly: true });
      }
      case 'qtyPer':
        return gcell.number(w.qtyPer, { display: fmtQty(w.qtyPer), ...(w.perBatch ? { theme: T_BATCH } : {}) });
      case 'scrap':
        return gcell.number(w.scrap, { display: `${Number(w.scrap || 0)}` });
      default:
        return gcell.text('', { readonly: true });
    }
  }, [usedIn]);

  const onWipEdited = useCallback(([col, row], newVal) => {
    const w = wipRef.current[row];
    if (!w) return;
    const id = WIP_COL_IDS[col];
    if (id === 'qtyPer' || id === 'scrap') updateNode(w.id, id, newVal.data);
  }, [updateNode]);

  // ---- Save (upsert only the touched lines + settings) ----
  const handleSave = async () => {
    const changedLines = [];
    for (const key of dirtyLines.current) {
      const [p, c, ln] = key.split('|');
      const line = (linesByParent[p] || []).find((l) => l.component_code === c && String(l.line_no) === ln);
      if (line) changedLines.push({ ...line, parent_code: p });
    }
    const changedSettings = [];
    for (const code of dirtySettings.current) {
      const m = mfg[code];
      if (m) changedSettings.push({ stock_code: code, basis: m.basis, batch_size: m.batchSize });
    }
    if (changedLines.length === 0 && changedSettings.length === 0) { setDirty(false); return; }

    setSaving(true);
    setSaveError('');
    try {
      const res = await bomService.saveBom({ lines: changedLines, settings: changedSettings });
      if (res?.success) {
        dirtyLines.current.clear();
        dirtySettings.current.clear();
        setDirty(false);
      } else {
        setSaveError(res?.message || 'Save failed.');
      }
    } catch (e) {
      setSaveError(e.response?.data?.message || e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // ---- Add a component to the current root item (commits immediately) ----
  const openAdd = async () => {
    setAddError('');
    setRequestedNote('');
    setAddOpen(true);
    if (!stockLoaded.current) {
      setStockLoading(true);
      try {
        const res = await bomService.getStockCodes();
        if (res?.success) {
          setStockOptions(res.data || []);
          stockLoaded.current = true;
        } else {
          setAddError(res?.message || 'Could not load stock codes.');
        }
      } catch (e) {
        setAddError(e.response?.data?.message || e.message || 'Could not load stock codes.');
      } finally {
        setStockLoading(false);
      }
    }
  };

  const rootChildCodes = useMemo(
    () => new Set((linesByParent[stockCode] || []).map((l) => l.component_code)),
    [linesByParent, stockCode]
  );

  const handleAdd = async (componentCode, qtyPer, scrap) => {
    if (!stockCode) return;
    const opt = stockOptions.find((o) => o.code === componentCode);
    const lineNo = (linesByParent[stockCode] || []).reduce((m, l) => Math.max(m, l.line_no || 0), 0) + 1;
    const newLine = {
      parent_code: stockCode,
      component_code: componentCode,
      line_no: lineNo,
      qty_per: Number(qtyPer) || 0,
      scrap_pct: Number(scrap) || 0,
      fixed_loss: 0,
      work_center: '',
      issue_warehouse: '',
      valid_periods: [...PERIODS],
      notes: '',
    };

    setLinesByParent((prev) => ({ ...prev, [stockCode]: [...(prev[stockCode] || []), newLine] }));
    if (opt) {
      setCatalog((prev) => (prev[componentCode]
        ? prev
        : { ...prev, [componentCode]: { description: opt.description, type: opt.type, uom: opt.uom } }));
    }

    setAdding(true);
    setAddError('');
    try {
      const res = await bomService.saveBom({ lines: [newLine] });
      if (res?.success) setAddOpen(false);
      else throw new Error(res?.message || 'Add failed.');
    } catch (e) {
      setLinesByParent((prev) => ({
        ...prev,
        [stockCode]: (prev[stockCode] || []).filter((l) => !(l.component_code === componentCode && l.line_no === lineNo)),
      }));
      setAddError(e.response?.data?.message || e.message || 'Add failed.');
    } finally {
      setAdding(false);
    }
  };

  // ---- Request a stock code that does not exist yet -------------------------
  // No BOM line is written: the code may never be issued, or be issued under a
  // different number. Procurement's approve step adds the line instead, using
  // the parent/qty/scrap carried on the request.
  const handleRequest = async (payload) => {
    if (!stockCode) return;
    setAdding(true);
    setAddError('');
    setRequestedNote('');
    try {
      const res = await requestedStockService.create({ ...payload, parent_code: stockCode });
      if (res?.success) {
        setRequestedNote(
          `${res.data.stock_code} sent to procurement. It will be added to ${stockCode} once approved.`
        );
      } else {
        throw new Error(res?.message || 'Request failed.');
      }
    } catch (e) {
      setAddError(e.response?.data?.message || e.message || 'Request failed.');
    } finally {
      setAdding(false);
    }
  };

  const selectClass =
    'text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 ' +
    'text-slate-700 dark:text-slate-100 px-2 py-1 outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50';

  const placeholder = (msg, tone = 'slate') => (
    <div className={`h-full rounded-xl border bg-white dark:bg-slate-800 flex items-center justify-center ${
      tone === 'red' ? 'border-red-200 dark:border-red-500/30' : 'border-slate-200 dark:border-slate-700'
    }`}>
      <div className="text-center px-8 text-sm text-slate-400">{msg}</div>
    </div>
  );

  // ---- Body ----------------------------------------------------------------
  let body;
  if (!selectedVersion || !selectedSite) {
    body = placeholder(
      !selectedVersion && !selectedSite
        ? 'Select a budget version and a site to load its bill of materials.'
        : !selectedVersion
          ? 'Select a budget version to load its bill of materials.'
          : 'Select a site — a BOM is captured per site.'
    );
  } else if (loading) {
    body = placeholder(<span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading bill of materials…</span>);
  } else if (loadError) {
    body = placeholder(
      <>
        <p className="m-0 text-red-600 dark:text-red-400">{loadError}</p>
        <button type="button" onClick={load}
          className="mt-3 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">
          Retry
        </button>
      </>, 'red'
    );
  } else if (!rootItem) {
    body = placeholder(
      <>
        {existingBomParents.length === 0
          ? 'No bills of materials for this site and version yet.'
          : 'Select a BOM above to open it.'}<br />
        <span className="text-xs">Use <span className="font-semibold">New BOM</span> to start one from a blank canvas.</span>
      </>
    );
  } else {
    body = (
      <div className="h-full flex gap-4">
        {/* Structure grid */}
        <div className="flex-1 min-w-0 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden flex flex-col">
          {/* Root context strip — item, batch mode, where-used. */}
          <div className="shrink-0 px-3.5 py-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              {stockCode} · {rootItem.description}
            </span>
            <span className="text-[11px] text-slate-400">
              {rootIsBatch
                ? `direct components per batch of ${fmtInt(rootMode.batchSize)} ${rootItem.uom}`
                : `per 1 ${rootItem.uom}`}
            </span>
            {rootItem.type === 'WIP' && rootUsedIn.length > 0 && (
              <span className="text-[11px] text-slate-400">
                · used in <span className="font-semibold text-slate-500 dark:text-slate-300">{rootUsedIn.join(', ')}</span>
              </span>
            )}
            <span className="ml-auto flex items-center gap-1.5">
              <span className="text-[10px] text-slate-400 uppercase tracking-wide">Component input</span>
              <button type="button" onClick={() => patchMfg(stockCode, { basis: 'unit' })}
                className={pillClass(!rootIsBatch)}>Per unit</button>
              <button type="button" onClick={() => patchMfg(stockCode, { basis: 'batch', batchSize: rootMode?.batchSize || 1000 })}
                className={pillClass(rootIsBatch || rootMode?.basis === 'batch')}>Per batch</button>
              {rootMode?.basis === 'batch' && (
                <input type="number" step="1" min="0" title={`Batch size (${rootItem.uom})`}
                  className={inputClass} value={rootMode.batchSize}
                  onChange={(e) => patchMfg(stockCode, { batchSize: Number(e.target.value) || 0 })} />
              )}
            </span>
          </div>

          {rows.length === 0 ? (
            <p className="px-8 py-10 text-center text-sm text-slate-400">
              Blank canvas — no components yet.<br />
              <span className="text-xs">Use <span className="font-semibold">Add component</span> above to add the first stock code.</span>
            </p>
          ) : (
            <MantleDataGrid
              gridId="bom-structure"
              columns={COLUMNS}
              rowCount={rows.length}
              getCellContent={getCellContent}
              onCellEdited={onCellEdited}
              onCellClicked={onCellClicked}
              freezeColumns={1}
              hint={`Click a Component to expand/collapse · ⋯ (or the Flags cell) opens the line detail for validity, batch and notes · cyan = captured per batch · ${
                rootIsBatch ? 'Ext Qty is per batch of the root item' : 'Ext Qty converts everything back to one unit of the root item'
              }.`}
            />
          )}
        </div>

        {/* Stage losses — the same grid component, shrink-wrapped to its rows. */}
        <div className="w-[460px] shrink-0 max-h-full flex flex-col rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
          <div className="shrink-0 px-3.5 py-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60">
            <span className="text-[10px] font-bold tracking-wider text-slate-500 dark:text-slate-400 uppercase">Stage Losses</span>
            <p className="m-0 text-[11px] text-slate-400">Qty per and scrap of each WIP stage — edit without opening the tree.</p>
          </div>

          {wipStages.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-slate-400">No WIP stages below this item.</p>
          ) : (
            <MantleDataGrid
              gridId="bom-wip"
              fitHeight
              columns={WIP_COLUMNS}
              rowCount={wipStages.length}
              getCellContent={getWipCell}
              onCellEdited={onWipEdited}
              freezeColumns={1}
            />
          )}

          {wipStages.length > 0 && (
            <div className="shrink-0 px-3.5 py-2 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">Compound loss across stages</span>
              <span className={`text-sm font-bold font-mono ${compoundScrap > 5 ? 'text-red-500' : 'text-slate-800 dark:text-slate-100'}`}>
                {compoundScrap.toFixed(2)}%
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      {/* Compact header — same pattern as the Purchase pricing pages. */}
      <div className="shrink-0 px-6 pt-4 pb-3">
        <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
          <h1
            className="m-0 mr-1 text-lg font-bold leading-7 tracking-tight text-slate-900 dark:text-slate-100"
            title="Define the BOM of any manufactured item — finished goods for packaging teams, WIP/bulk for process teams."
          >
            Bill of Materials
          </h1>

          <Field dense label="Scope" hint="Which manufactured items are listed">
            <div className="flex items-center gap-1">
              {SCOPES.map((s) => (
                <button key={s.code} type="button" onClick={() => handleScope(s.code)}
                  className={`px-1.5 py-0.5 text-[11px] rounded border transition-colors ${
                    scope === s.code
                      ? 'bg-slate-900 text-white border-slate-900 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200'
                      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
                  }`}>
                  {s.label}
                </button>
              ))}
            </div>
          </Field>

          <Field dense label="BOM" hint="Bills of materials at this site">
            <select className={selectClass} value={stockCode} onChange={(e) => handleSelect(e.target.value)}
              disabled={loading || !selectedVersion || !selectedSite}>
              <option value="">
                {loading ? 'Loading…' : scopedItems.length === 0 ? 'No BOMs yet — use New BOM' : 'Select a BOM…'}
              </option>
              {scopedItems.map((code) => (
                <option key={code} value={code}>
                  {code} · {catalog[code].description}{linesByParent[code] ? '' : '  (new)'}
                </option>
              ))}
            </select>
          </Field>

          {selectedVersion && selectedSite && !loading && (
            <button type="button" onClick={() => setNewOpen(true)} disabled={newBomCandidates.length === 0}
              title={newBomCandidates.length === 0 ? 'Every FG/WIP item already has a BOM at this site' : 'Start a new BOM from a blank canvas'}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-md border transition-colors ${
                newBomCandidates.length === 0
                  ? 'border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-600 cursor-not-allowed'
                  : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700'
              }`}>
              <Plus className="w-3 h-3" /> New BOM
            </button>
          )}

          {rootItem && (
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={openAdd}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-md border border-slate-900 dark:border-slate-200 bg-slate-900 dark:bg-slate-200 text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-white transition-colors">
                <Plus className="w-3 h-3" /> Add component
              </button>
              <button type="button" onClick={expandAll}
                className="px-2.5 py-1 text-[11px] font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">
                Expand all
              </button>
              <button type="button" onClick={collapseAll}
                className="px-2.5 py-1 text-[11px] font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">
                Collapse all
              </button>
              <button type="button" onClick={handleSave} disabled={!dirty || saving}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-md text-white shadow-sm transition-colors ${
                  dirty && !saving ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 dark:bg-slate-600'
                }`}>
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                {saving ? 'Saving…' : 'Save'}
              </button>
              {saveError && <span className="text-[11px] text-red-600 dark:text-red-400">{saveError}</span>}
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 px-6 pb-4">{body}</div>

      <LineDetailModal
        node={detailNode}
        mfg={mfg}
        warehouses={warehouses}
        isManufactured={isManufactured}
        fyStartMonth={fyStartMonth}
        onPatch={(patch) => patchNodeById(detailNode.id, patch)}
        onPatchMfg={patchMfg}
        onClose={() => setDetailId(null)}
      />

      {newOpen && (
        <NewBomModal candidates={newBomCandidates} onStart={startNewBom} onClose={() => setNewOpen(false)} />
      )}

      {addOpen && rootItem && (
        <AddComponentModal
          rootCode={stockCode}
          rootDescription={rootItem.description}
          existingChildren={rootChildCodes}
          options={stockOptions}
          loading={stockLoading}
          busy={adding}
          error={addError}
          requestedNote={requestedNote}
          onAdd={handleAdd}
          onRequest={handleRequest}
          onClose={() => { if (!adding) { setAddOpen(false); setAddError(''); setRequestedNote(''); } }}
        />
      )}
    </div>
  );
};

export default BillOfMaterials;
